import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Loader2, HardHat, Pencil } from "lucide-react";
import { toast } from "sonner";
import { SCHEMES } from "@/lib/constants";

const EMPTY = { name: "", trustmark_number: "", measure_codes: [], schemes: [] };

export default function AdminInstallers() {
  const [installers, setInstallers] = useState(null);
  const [measures, setMeasures] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/installers").then((r) => setInstallers(r.data));
  useEffect(() => {
    load();
    api.get("/measures").then((r) => setMeasures(r.data.filter((m) => m.active))).catch(() => {});
  }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (ins) => { setForm({ name: ins.name, trustmark_number: ins.trustmark_number, measure_codes: ins.measure_codes || [], schemes: ins.schemes || [] }); setEditId(ins.id); setOpen(true); };

  const toggle = (key, val) => setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));

  const save = async () => {
    if (!form.name || !form.trustmark_number) return toast.error("Name and TrustMark number are required");
    if (form.measure_codes.length === 0) return toast.error("Select at least one measure");
    try {
      if (editId) await api.put(`/installers/${editId}`, { id: editId, ...form });
      else await api.post("/installers", form);
      toast.success(editId ? "Installer updated" : "Installer added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (ins) => {
    if (!window.confirm(`Remove ${ins.name}?`)) return;
    try {
      await api.delete(`/installers/${ins.id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  if (!installers)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-accent" /></div>;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Directory</p>
          <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-slate-900">Installers</h1>
          <p className="mt-1 text-sm text-slate-500">Approved installers &amp; their TrustMark numbers. Engineers pick these on-site to auto-fill the number.</p>
        </div>
        <Button onClick={openNew} className="field-lg bg-accent font-bold hover:bg-accent/90" data-testid="add-installer-btn">
          <Plus className="mr-2 h-4 w-4" /> Add installer
        </Button>
      </div>

      <div className="space-y-3">
        {installers.length === 0 && <p className="text-sm text-slate-400">No installers yet.</p>}
        {installers.map((ins) => (
          <Card key={ins.id} className="flex items-start justify-between border-slate-200 p-4 shadow-sm" data-testid={`installer-row-${ins.id}`}>
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-100"><HardHat className="h-5 w-5 text-accent" /></div>
              <div>
                <div className="font-heading font-bold text-slate-900">{ins.name}</div>
                <div className="text-sm text-slate-500">TrustMark: <span className="font-semibold text-slate-700">{ins.trustmark_number}</span></div>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {(ins.measure_codes || []).map((c) => <Badge key={c} variant="outline" className="border-accent/50 text-[10px] text-accent">{c}</Badge>)}
                  {(ins.schemes || []).map((s) => <Badge key={s} variant="outline" className="border-slate-300 text-[10px] text-slate-500">{s}</Badge>)}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button size="icon" variant="ghost" onClick={() => openEdit(ins)} className="h-9 w-9 text-slate-500" data-testid={`edit-installer-${ins.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => remove(ins)} className="h-9 w-9 text-destructive" data-testid={`delete-installer-${ins.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit installer" : "Add installer"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Company name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="field-lg" data-testid="installer-form-name" />
            </div>
            <div className="space-y-1.5">
              <Label>TrustMark number</Label>
              <Input value={form.trustmark_number} onChange={(e) => setForm({ ...form, trustmark_number: e.target.value })} className="field-lg" data-testid="installer-form-tm" />
            </div>
            <div className="space-y-1.5">
              <Label>Measures they carry out</Label>
              <div className="flex flex-wrap gap-2">
                {measures.map((m) => {
                  const on = form.measure_codes.includes(m.code);
                  return (
                    <button key={m.code} type="button" onClick={() => toggle("measure_codes", m.code)} className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${on ? "border-accent bg-accent/10 text-accent" : "border-slate-300 text-slate-500 hover:bg-slate-50"}`} data-testid={`installer-measure-${m.code}`}>
                      {m.code} · {m.name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Schemes (leave empty for all)</Label>
              <div className="flex flex-wrap gap-2">
                {SCHEMES.map((s) => {
                  const on = form.schemes.includes(s);
                  return (
                    <button key={s} type="button" onClick={() => toggle("schemes", s)} className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${on ? "border-primary bg-primary/10 text-primary" : "border-slate-300 text-slate-500 hover:bg-slate-50"}`} data-testid={`installer-scheme-${s.slice(0, 8)}`}>
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} className="bg-accent font-bold hover:bg-accent/90" data-testid="save-installer-btn">{editId ? "Save changes" : "Add installer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
